




















import Debug from 'debug';
import { Vue, Component, Prop, Emit } from 'vue-property-decorator';
import i18n from '@/plugins/i18n';
import {uniqueId} from 'lodash';
import * as Api from "@/app/interaction/api";
import { QuestionInteractionContent } from './question-content'
import { QuestionInteractionState } from './question-interaction-state'
import QuestionComponent2 from '@/app/question/components/question-component2.vue';
import { QuestionAnsweredPayload, QuestionState } from '@/app/question/components/question-component2.vue'
import { QuestionStateMachineData, defaultQuestionStateMachineData, saveQuestionState, loadQuestionState,
         QuestionStateMachine, QuestionPhase,
         isQuestionPhaseEvaluated } from '@/app/question/domain/state-machine'

const debug = Debug('question-interaction-component');

@Component({
  components: {
    QuestionComponent2
  }
})
export default class QuestionInteractionComponent extends Vue implements Api.InteractiveComponent<QuestionInteractionState, QuestionInteractionContent> {
  
  @Prop({
    required: true
  })
  public meta: Api.InteractionComponentMeta;
  
  @Prop({
    required: true
  })
  public content: QuestionInteractionContent;
  
  @Prop({
    required: true
  })
  public state: QuestionInteractionState | null;
  
  private data: QuestionStateMachineData = defaultQuestionStateMachineData(0);
  
  private stateMachine: QuestionStateMachine;
  
  created() {
    const { question } = this.content

    debug('created', question.id, question, 'state', this.state)

    const questionScore = { min: question.minScore,
                            max: question.maxScore,
                            content: {
                              min: question.contentScore.min,
                              max: question.contentScore.max
                            }
                          }

    let data
    if (this.state) {
      debug('loading state at create', JSON.stringify(this.state))
      data = loadQuestionState(this.state, questionScore)
    } else {
      data = defaultQuestionStateMachineData(questionScore.min)
    }
    
    this.data = data
    this.stateMachine = new QuestionStateMachine(this.data, {
      score: questionScore
    });
  }

  private get questionViewState() {
    const phase = this.data.phase;
    debug('determining question view state in phase', phase)
    
    const state = {
      evaluated: isQuestionPhaseEvaluated(phase),
      showEvaluateButton: inState('not-answered', 'answered'),
      canEvaluate: inState('answered'),
      questionState: this.data.state,
      questionAnswer: this.data.answer,
      evaluationResult: {
        score: this.data.evaluation.details.score,
        result: this.data.evaluation.result
      },
      showSolution: inState('solution-shown')
    }
    debug('current question view state', state);
    
    function inState(...phases: QuestionPhase[]) {
      return phases.includes(phase)
    }
    
    return state
  }
  
  private handleAnswerChanged(payload: QuestionAnsweredPayload) {
    debug('handle-answer-changed', payload);
    
    this.stateMachine.updateAnswer(payload.answer);
    this.stateMachine.updateEvaluation(payload.evaluation);

    this.saveState({ setAsCompleted: false })
  }
  
  private handleStateChanged(payload: QuestionState) {
    debug('handle-state-changed', payload);
    
    this.stateMachine.updateState(payload);

    this.saveState({ setAsCompleted: false })
  }
  
  private handleEvaluate() {
    debug('handle-evaluate');
    
    this.stateMachine.evaluate()

    this.saveState({ setAsCompleted: true })
  }
  
  private handleShowSolution() {
    debug('handle-show-solution');
    
    this.stateMachine.showSolution();

    this.saveState({ setAsCompleted: false })
  }
  
  private handleReset() {
    debug('handle-reset');
    
    this.stateMachine.reset();

    this.saveState({ setAsCompleted: false })
  }

  private saveState(options: { setAsCompleted: boolean }) {
    debug('save-state')
    
    this.emitInteractionStateChanged()
    if (options.setAsCompleted) {
      this.emitInteractionResultChanged()
    }
  }

  @Emit('interaction-result-changed')
  public emitInteractionResultChanged() {

    const result: Api.Result = {
      progress: 1,
      status: 'completed'
    }
    return result      
  }

  @Emit('interaction-state-changed')
  public emitInteractionStateChanged() {

    const questionState = saveQuestionState(this.data)
    const type = this.content.question.type
    
    const state: QuestionInteractionState = {
      type,
      ...questionState
    }
    debug('emit-interaction-state-changed', state);

    return state
  }
}
